/* authSlice.ts */

/* Redux-Toolkit */
import {
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

/* Cookies */
import Cookies from "js-cookie";

/* Thunks */
import { loginWithCredentials } from "src/redux/thunks/auth/loginWithCredentialsThunk";
import { logoutWithRefreshToken } from "src/redux/thunks/auth/logoutWithRefreshTokenThunk";
import { loginWithGoogle } from "src/redux/thunks/auth/loginWithGoogleThunk";
import { loginWithOtp } from "src/redux/thunks/auth/loginWithOtpThunk";
import { refreshAccessToken } from "src/redux/thunks/auth/authThunk";
import { fetchUserRoles } from "src/redux/thunks/auth/userRolesThunk";
import { fetchAllowedCareSites } from "src/redux/thunks/auth/allowedCareSitesThunk";

/* Utils */
import { ClientError } from "src/utils/status-handling/ClientError";
import { isTwoFaErrorProps } from "src/utils/guards/error-handling";
import { Success } from "src/utils/status-handling/Success";

/* Constants */
import { initialState } from "src/shared/constants/auth/InitialState";
import { GlobalMessageType } from "src/shared/constants/common/GlobalMessage";
import { SuccessMessage } from "src/shared/constants/common/SuccessMessage";

/* Interfaces */
import {
  AuthProps,
  AuthSliceProps,
  TwoFaProps,
} from "src/shared/interfaces/common/Auth";

import { TokenType } from "src/shared/interfaces/api/Token";

export const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    /**
     * Clear the authentication state
     * and remove `ACCESS_TOKEN` and `REFRESH_TOKEN`
     * from the cookies.
     *
     * @param {AuthSliceProps} state
     * @return {void}
     */
    clearAuthState: (state: AuthSliceProps): void => {
      state.data = {
        isAuthenticated: false,
        expirationTime: null,
      };
      state.allowedCareSites = null;
      state.allowedCareSitesStatus = "idle";
      Cookies.remove(TokenType.ACCESS_TOKEN);
      Cookies.remove(TokenType.REFRESH_TOKEN);
    },
    clearError: (state) => {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    /**
     * loginWithCredentials
     *
     * @param {AuthSliceProps} state - The current state.
     * @returns {void}
     */
    builder.addCase(
      loginWithCredentials.pending,
      (state: AuthSliceProps): void => {
        state.isLoading = true;
      }
    );
    builder.addCase(
      loginWithCredentials.rejected,
      (
        state,
        action: PayloadAction<
          ClientError | TwoFaProps | undefined
        >
      ) => {
        state.isLoading = false;

        if (!action.payload) return;

        if (isTwoFaErrorProps(action.payload)) {
          state.twoFa = {
            credentials: action.payload.credentials,
            qrCode: action.payload.qrCode,
            qrCodeRequired: action.payload?.qrCodeRequired!,
            twoFaRequired: action.payload?.twoFaRequired!,
          };
        } else {
          state.error = action.payload;
        }
      }
    );
    builder.addCase(
      loginWithCredentials.fulfilled,
      (state, action: PayloadAction<AuthProps>) => {
        state.data = action.payload;
        state.isLoading = false;
        state.error = undefined;
      }
    );

    /**
     * loginWithOtp
     *
     * @param {AuthSliceProps} state - The current state.
     * @param {PayloadAction<AuthProps>} action - The action payload.
     * @returns {void}
     */
    builder.addCase(
      loginWithOtp.fulfilled,
      (
        state: AuthSliceProps,
        action: PayloadAction<AuthProps | undefined>
      ): void => {
        state.data = action.payload;
        state.isLoading = false;
        state.error = undefined;
      }
    );
    builder.addCase(
      loginWithOtp.rejected,
      (
        state,
        action: PayloadAction<ClientError | undefined>
      ) => {
        state.isLoading = false;
        state.error = action.payload;
      }
    );

    /**
     * loginWithGoogle
     *
     * @param {AuthSliceProps} state - The current state.
     * @returns {void}
     */
    builder.addCase(
      loginWithGoogle.pending,
      (state: AuthSliceProps): void => {
        state.isLoading = false;
      }
    );
    builder.addCase(
      loginWithGoogle.fulfilled,
      (state, action: PayloadAction<AuthProps>) => {
        state.data = action.payload;
        state.isLoading = false;
        state.error = undefined;
      }
    );
    builder.addCase(
      loginWithGoogle.rejected,
      (
        state,
        action: PayloadAction<ClientError | undefined>
      ) => {
        state.isLoading = false;
        state.error = action.payload;
      }
    );

    /**
     * refreshAccessToken
     **/
    builder.addCase(refreshAccessToken.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      refreshAccessToken.fulfilled,
      (state, action: PayloadAction<AuthProps>) => {
        state.data = action.payload;
        state.isLoading = false;
      }
    );
    builder.addCase(
      refreshAccessToken.rejected,
      (
        state,
        action: PayloadAction<ClientError | undefined>
      ) => {
        state.isLoading = false;
        state.error = action.payload;
      }
    );

    /**
     * logoutWithRefreshToken
     *
     * @param {AuthSliceProps} state
     * @returns {void}
     */
    builder.addCase(
      logoutWithRefreshToken.pending,
      (state: AuthSliceProps): void => {
        state.isLoading = true;
      }
    );
    /**
     * @param {AuthSliceProps} state
     * @param {PayloadAction<AuthProps>} action
     * @returns {void}
     */
    builder.addCase(
      logoutWithRefreshToken.fulfilled,
      (
        state: AuthSliceProps,
        action: PayloadAction<AuthProps>
      ): void => {
        state.data = action.payload;

        // After logout, set information to initial state
        state.twoFa = initialState.twoFa;
        state.isLoading = false;
        state.error = undefined;

        state.success = new Success({
          type: GlobalMessageType.SUCCESS,
          message: SuccessMessage.LOGOUT_SUCCESSFUL,
        });
      }
    );

    /**
     * @param {AuthSliceProps} state
     * @param {PayloadAction<ClientError | undefined>} action
     * @returns {void}
     */
    builder.addCase(
      logoutWithRefreshToken.rejected,
      (
        state: AuthSliceProps,
        action: PayloadAction<ClientError | undefined>
      ): void => {
        state.isLoading = false;
        state.error = action.payload;
      }
    );

    /**
     * Fetch user roles.
     *
     * @param {AuthSliceProps} state
     * @param {<string[] | undefined>} action
     */
    builder.addCase(
      fetchUserRoles.fulfilled,
      (
        state,
        action: PayloadAction<string[] | undefined>
      ) => {
        if (action.payload) {
          state.roles = action.payload;
        }
      }
    );

    /**
     * Fetch allowed care sites.
     *
     * @param {AuthSliceProps} state
     * @param {PayloadAction<string[]>} action
     */
    builder.addCase(
      fetchAllowedCareSites.pending,
      (state) => {
        // Null out during refetch so the hook blocks requests with stale
        // permissions while the fresh list is in flight.
        state.allowedCareSites = null;
        state.allowedCareSitesStatus = "loading";
      }
    );

    builder.addCase(
      fetchAllowedCareSites.fulfilled,
      (state, action: PayloadAction<string[]>) => {
        if (!state.data?.isAuthenticated) return;
        state.allowedCareSites = action.payload;
        state.allowedCareSitesStatus = "success";
      }
    );

    builder.addCase(
      fetchAllowedCareSites.rejected,
      (state) => {
        if (!state.data?.isAuthenticated) return;
        // Reset to null so the hook blocks all insights requests
        // rather than silently sending unrestricted-looking requests.
        state.allowedCareSites = null;
        state.allowedCareSitesStatus = "error";
      }
    );
  },
});

/* Actions */
export const { clearAuthState, clearError } =
  authSlice.actions;

/* Reducer */
export default authSlice.reducer;
